import { useEffect, useState } from 'react';
import { api } from '../hooks/useApi';
import { Card, Button, Input, Spinner, Badge, EmptyState, PageHeader } from '../components/ui';
import type { Site, TimelineEntry } from '@ns-b2b/shared';

export function FollowUp() {
    const [sites, setSites] = useState<Site[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [siteTimeline, setSiteTimeline] = useState<TimelineEntry[]>([]);
    const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);

    useEffect(() => {
        loadSites();
    }, []);

    const loadSites = async (query?: string) => {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        params.set('limit', '50');
        const { data } = await api<{ data: Site[] }>(`/api/sites?${params.toString()}`);
        if (data) setSites((data as unknown as { data: Site[] }).data || []);
        setIsLoading(false);
    };

    const handleSearch = () => loadSites(searchQuery);

    const loadSiteDetail = async (site: Site) => {
        setSelectedSite(site);
        setIsLoadingTimeline(true);
        const { data } = await api<{ site: Site; timeline: TimelineEntry[] }>(`/api/sites/${site.id}`);
        if (data) {
            const response = data as unknown as { data: { site: Site; timeline: TimelineEntry[] } };
            setSiteTimeline(response.data.timeline || []);
        }
        setIsLoadingTimeline(false);
    };

    return (
        <div className="space-y-6">
            <PageHeader title="ติดตามลูกค้า" subtitle="ค้นหาและติดตามลูกค้าที่บันทึกไว้" />

            {/* Search */}
            <Card padding="md">
                <div className="flex gap-3">
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="ค้นหาด้วยชื่อ, เบอร์โทร, หรือที่อยู่..."
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch} variant="primary">
                        ค้นหา
                    </Button>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sites List */}
                <div>
                    <h2 className="text-lg font-semibold text-neutral-900 mb-4">รายการลูกค้า</h2>
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <Spinner />
                        </div>
                    ) : sites.length === 0 ? (
                        <Card padding="lg">
                            <EmptyState icon="🔍" title="ไม่พบข้อมูล" description="ลองค้นหาด้วยคำค้นอื่น" />
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {sites.map((site) => (
                                <div
                                    key={site.id}
                                    className={`card-interactive p-4 ${selectedSite?.id === site.id ? 'ring-2 ring-primary' : ''}`}
                                    onClick={() => loadSiteDetail(site)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="font-semibold text-neutral-900">
                                                {site.customer_name || 'ไม่ระบุชื่อ'}
                                            </h3>
                                            <p className="text-sm text-neutral-500 mt-1">{site.phone || '-'}</p>
                                            {site.jobsite && (
                                                <p className="text-sm text-neutral-500">{site.jobsite}</p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <Badge variant="primary">ทีม {site.team}</Badge>
                                            <p className="text-xs text-neutral-400 mt-2">{site.visit_count} ครั้ง</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Timeline */}
                <div>
                    <h2 className="text-lg font-semibold text-neutral-900 mb-4">ประวัติการติดต่อ</h2>
                    {selectedSite ? (
                        <Card padding="lg">
                            <div className="mb-6 pb-4 border-b border-neutral-100">
                                <h3 className="text-lg font-bold text-neutral-900">{selectedSite.customer_name}</h3>
                                <p className="text-neutral-500 text-sm">
                                    {selectedSite.phone} • {selectedSite.province || 'ไม่ระบุจังหวัด'}
                                </p>
                            </div>

                            {isLoadingTimeline ? (
                                <div className="flex justify-center py-12"><Spinner /></div>
                            ) : siteTimeline.length === 0 ? (
                                <EmptyState icon="📭" title="ยังไม่มีประวัติ" />
                            ) : (
                                <div className="space-y-4">
                                    {siteTimeline.map((entry) => (
                                        <div key={entry.id} className="relative pl-6 border-l-2 border-neutral-200">
                                            <div className="absolute left-[-5px] top-0 w-2 h-2 bg-primary rounded-full" />
                                            <div className="pb-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge variant={entry.type === 'visit' ? 'primary' : 'success'}>
                                                        {entry.type === 'visit' ? 'หน้างาน' : 'ติดตาม'}
                                                    </Badge>
                                                    <span className="text-xs text-neutral-400">{entry.date}</span>
                                                </div>
                                                <p className="font-medium text-neutral-800">{entry.summary}</p>
                                                {entry.detail && <p className="text-sm text-neutral-500 mt-1">{entry.detail}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    ) : (
                        <Card padding="lg">
                            <EmptyState icon="👈" title="เลือกลูกค้า" description="คลิกที่รายการด้านซ้าย" />
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
